import { MAX_SAMPLE_BYTES } from '../../domain/schemas';

/**
 * File-type checks for imported samples. Only formats with a reliable browser decoder
 * are accepted: WAV everywhere, MP3 and Ogg when the browser can decode them (checked
 * by actually decoding; nothing is promised up front), plus the container types that
 * MediaRecorder produces for recordings (WebM/Ogg Opus, MP4 AAC in Safari).
 */
export type AudioFormat = 'wav' | 'mp3' | 'ogg' | 'webm' | 'mp4';

export const FORMAT_MIME: Record<AudioFormat, string> = {
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
  mp4: 'audio/mp4',
};

/** What the import picker offers (recordings are produced in-app, not imported). */
export const IMPORT_ACCEPT = '.wav,.wave,.mp3,.ogg,.oga,audio/wav,audio/x-wav,audio/wave,audio/mpeg,audio/mp3,audio/ogg';

const EXTENSIONS: Record<string, AudioFormat> = {
  wav: 'wav',
  wave: 'wav',
  mp3: 'mp3',
  ogg: 'ogg',
  oga: 'ogg',
};

const startsWith = (bytes: Uint8Array, text: string, at = 0) =>
  [...text].every((ch, i) => bytes[at + i] === ch.charCodeAt(0));

/** Identifies an audio container from its first bytes (≥ 12 bytes). */
export function sniffFormat(bytes: Uint8Array): AudioFormat | null {
  if (startsWith(bytes, 'RIFF') && startsWith(bytes, 'WAVE', 8)) return 'wav';
  if (startsWith(bytes, 'OggS')) return 'ogg';
  if (startsWith(bytes, 'ID3')) return 'mp3';
  // MPEG audio frame sync: 11 set bits.
  if (bytes[0] === 0xff && ((bytes[1] ?? 0) & 0xe0) === 0xe0) return 'mp3';
  if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) return 'webm';
  if (startsWith(bytes, 'ftyp', 4)) return 'mp4';
  return null;
}

export class AudioFileError extends Error {
  readonly code: 'unsupported' | 'too-large' | 'empty' | 'mismatch';
  constructor(code: AudioFileError['code'], message: string) {
    super(message);
    this.name = 'AudioFileError';
    this.code = code;
  }
}

/**
 * Validates an import by size, extension and actual content (the browser-supplied MIME
 * type is often empty or wrong, so the bytes decide). Returns the canonical MIME type.
 */
export function validateImport(name: string, sizeBytes: number, header: Uint8Array): { format: AudioFormat; mimeType: string } {
  if (sizeBytes === 0) throw new AudioFileError('empty', `“${name}” is empty.`);
  if (sizeBytes > MAX_SAMPLE_BYTES) {
    throw new AudioFileError('too-large', `“${name}” is ${(sizeBytes / 1048576).toFixed(1)} MB; samples are limited to ${MAX_SAMPLE_BYTES / 1048576} MB.`);
  }
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase();
  const byName = ext ? EXTENSIONS[ext] : undefined;
  const byContent = sniffFormat(header);
  if (!byContent || byContent === 'webm' || byContent === 'mp4') {
    throw new AudioFileError(
      'unsupported',
      `“${name}” is not a supported audio file. Import WAV, or MP3/OGG if your browser can play them.`,
    );
  }
  if (byName && byName !== byContent) {
    throw new AudioFileError('mismatch', `“${name}” is named .${ext} but contains ${byContent.toUpperCase()} data.`);
  }
  return { format: byContent, mimeType: FORMAT_MIME[byContent] };
}
